import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, FileText, Trash2, Pencil, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Templates() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();

  const load = () => api.get("/templates").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => { await api.delete(`/templates/${id}`); toast.success("Removed"); load(); };

  const duplicate = async (t) => {
    await api.post("/templates", { name: t.name + " (copy)", subject: t.subject, body: t.body, blocks: t.blocks, category: t.category });
    toast.success("Duplicated");
    load();
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="display text-4xl">Templates</h1>
          <p className="text-slate-500 mt-2">Drag-and-drop newsletter designer. Or paste your HTML.</p>
        </div>
        <Button data-testid="new-template-btn" onClick={() => nav("/templates/builder")} className="bg-[#0044FF] hover:bg-[#0033CC] gap-2"><Plus className="w-4 h-4" /> New Template</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t, i) => (
          <Card key={t.id} data-testid={`template-card-${i}`} className="p-5 border-slate-200 hover:shadow-md hover:-translate-y-[2px] transition-all duration-200 group">
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center"><FileText className="w-5 h-5 text-[#0044FF]" /></div>
              <Badge variant="outline" className="text-xs border-slate-200 mono">{t.category}</Badge>
            </div>
            <h3 className="font-semibold mb-1" style={{ fontFamily: "Inter, sans-serif" }}>{t.name}</h3>
            <div className="text-sm text-slate-500 line-clamp-2 mb-3" style={{ fontFamily: "Inter, sans-serif" }}>{t.subject}</div>
            <div
              className="text-xs text-slate-500 line-clamp-3 bg-slate-50 p-2 rounded border border-slate-200"
              style={{ fontFamily: "Inter, sans-serif" }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize((t.body || "").replace(/<style[\s\S]*?<\/style>/g, "").slice(0, 200)) }}
            />
            <div className="flex gap-2 mt-4">
              <Button data-testid={`template-edit-${i}`} size="sm" variant="outline" onClick={() => nav(`/templates/builder/${t.id}`)} className="flex-1 gap-1"><Pencil className="w-3 h-3" /> Edit</Button>
              <Button data-testid={`template-copy-${i}`} size="icon" variant="ghost" onClick={() => duplicate(t)}><Copy className="w-4 h-4 text-slate-400" /></Button>
              <Button data-testid={`template-delete-${i}`} size="icon" variant="ghost" onClick={() => remove(t.id)}><Trash2 className="w-4 h-4 text-slate-400" /></Button>
            </div>
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="col-span-full p-10 text-center text-slate-400">No templates yet — create your first one</Card>
        )}
      </div>
    </div>
  );
}
